import { catchError, concatMap, defer, from, map, Observable, of, reduce } from 'rxjs';

/**
 * Bilanz eines Mehrfach-Löschvorgangs von Devices.
 * - `deleted`: Anzahl erfolgreich gelöschter Devices
 * - `failedIds`: IDs der Devices, deren Löschung fehlgeschlagen ist
 */
export interface DeleteAllDevicesResult {
  deleted: number;
  failedIds: string[];
}

/**
 * Transport-neutrale Orchestrierung für "alle Devices löschen": iteriert sequentiell über
 * `deviceIds`, ruft je `deleteOne` auf, bricht bei Fehlern NICHT ab und liefert eine Bilanz.
 *
 * Gemeinsame Codebasis für den Angular-`OrgAdminEndpointsService` (HttpClient) und den
 * E2E-Device-Sweep (`fetch`): beide liefern nur ihren `deleteOne`-Transport, die Schleifen-/
 * Bilanz-/Cancellation-Logik (defer → frischer Seed pro Subscription) liegt hier.
 *
 * @param deviceIds zu löschende Device-IDs
 * @param deleteOne Transport-Callback: löscht genau ein Device; ein Fehler (Observable-Error)
 *                  zählt als fehlgeschlagen und landet in {@link DeleteAllDevicesResult.failedIds}
 */
export function deleteDevicesSequentially(
  deviceIds: string[],
  deleteOne: (deviceId: string) => Observable<unknown>
): Observable<DeleteAllDevicesResult> {
  // defer: frischer Bilanz-Seed pro Subscription – sonst würde das mutierte
  // Seed-Objekt über mehrere Subscriptions geteilt und die Zähler akkumulieren.
  return defer(() =>
    from(deviceIds).pipe(
      concatMap(deviceId =>
        deleteOne(deviceId).pipe(
          map(() => ({ deviceId, ok: true })),
          catchError(() => of({ deviceId, ok: false }))
        )
      ),
      reduce<{ deviceId: string; ok: boolean }, DeleteAllDevicesResult>(
        (acc, r) => {
          if (r.ok) {
            acc.deleted++;
          } else {
            acc.failedIds.push(r.deviceId);
          }
          return acc;
        },
        { deleted: 0, failedIds: [] }
      )
    )
  );
}
